// src/features/merchantConfig/components/ConfigDetailPanel.jsx
//
// Detail panel renderer: header ribbon, audit ribbon, body (ValueBlockRenderer),
// raw-JSON escape hatch, and the Phase-2 EditToolbar footer.
//
// Phase 2 responsibilities:
//   * Reads `mode` from EditStateContext. In view mode the renderer reads the
//     original value block; in edit mode it reads the draft.
//   * On Save, diffs draft vs original to build the minimal PATCH body (per
//     OMS API §5.5 semantics) and fires the mutation.
//   * Surface server validation errors back to the toolbar / field rows.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, Code, Copy, Check, ChevronDown, ChevronUp } from "lucide-react";
import { useToast } from "../../../components/toast/ToastProvider";
import { humanizeLabel } from "../lib/humanizeLabel";
import { diffForPatch, parseFieldNameToPath } from "../lib/jsonPath";
import { humanizeErrorCode } from "../lib/errorMessages";
import { isCategoryEditable } from "../lib/editFieldPermissions";
import ValueBlockRenderer from "./ValueBlockRenderer";
import NotProvisionedEmptyState from "./states/NotProvisionedEmptyState";
import ErrorState from "./states/ErrorState";
import { DetailPanelSkeleton } from "./states/LoadingSkeleton";
import EditToolbar from "./EditToolbar";
import UnsavedChangesPrompt, {
  useBeforeUnloadGuard,
} from "./UnsavedChangesPrompt";
import { useEditState } from "../state/EditStateContext";
import { usePatchMerchantConfig } from "../hooks/usePatchMerchantConfig";

/* ---------- Audit ribbon ---------- */

function formatAuditTimestamp(raw) {
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(d);
  } catch {
    return raw;
  }
}

function relativeTimeFrom(raw) {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  const min = Math.round(sec / 60);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);

  try {
    // eslint-disable-next-line no-undef
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    if (Math.abs(sec) < 60) return rtf.format(-sec, "second");
    if (Math.abs(min) < 60) return rtf.format(-min, "minute");
    if (Math.abs(hr) < 24) return rtf.format(-hr, "hour");
    return rtf.format(-day, "day");
  } catch {
    return null;
  }
}

function AuditRibbon({
  updatedDateTime,
  updatedBy,
  onRefresh,
  isRefetching,
  refreshDisabled,
  refreshDisabledReason,
}) {
  const formatted = formatAuditTimestamp(updatedDateTime);
  const relative = relativeTimeFrom(updatedDateTime);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50 px-4 py-2.5 text-xs">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-700">
        {updatedDateTime ? (
          <>
            <span className="font-medium text-gray-500">Updated</span>
            <span title={updatedDateTime} className="text-gray-900">
              {relative ?? formatted}
            </span>
            {relative && formatted && (
              <span className="text-gray-400">({formatted})</span>
            )}
          </>
        ) : (
          <span className="text-gray-500">No audit information available</span>
        )}
        {updatedBy && (
          <>
            <span className="text-gray-400">·</span>
            <span className="font-medium text-gray-500">by</span>
            <span className="font-mono text-gray-900">{updatedBy}</span>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onRefresh}
        disabled={isRefetching || refreshDisabled}
        title={refreshDisabledReason ?? undefined}
        aria-label="Refresh this configuration"
        className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <RefreshCw
          className={`h-3.5 w-3.5 ${isRefetching ? "animate-spin" : ""}`}
          aria-hidden="true"
        />
        {isRefetching ? "Refreshing…" : "Refresh"}
      </button>
    </div>
  );
}

/* ---------- Raw JSON toggle ---------- */

function RawJsonToggle({ envelope }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { showToast } = useToast();

  const pretty = JSON.stringify(envelope, null, 2);

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(pretty);
      setCopied(true);
      showToast("Raw JSON copied to clipboard", "success", 1500);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      showToast("Couldn't copy raw JSON", "error", 2000);
    }
  }, [pretty, showToast]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <span className="inline-flex items-center gap-2">
          <Code className="h-4 w-4 text-gray-500" aria-hidden="true" />
          View raw JSON
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-gray-500" aria-hidden="true" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500" aria-hidden="true" />
        )}
      </button>
      {open && (
        <div className="border-t border-gray-100">
          <div className="flex items-center justify-end border-b border-gray-100 bg-gray-50 px-3 py-1.5">
            <button
              type="button"
              onClick={onCopy}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <pre className="max-h-[480px] overflow-auto px-4 py-3 font-mono text-xs leading-relaxed text-gray-800">
            {pretty}
          </pre>
        </div>
      )}
    </div>
  );
}

/* ---------- Main detail panel ---------- */

/**
 * @param {{
 *   catalogEntry: import('../lib/configKeyCatalog').MerchantConfigCatalogEntry | undefined,
 *   merchantId: string,
 *   queryState: {
 *     status: "loading" | "success" | "error",
 *     isFetching: boolean,
 *     data: Record<string, unknown> | null,
 *     error: Object | null,
 *   },
 *   onRefresh: () => void,
 *   onContactSupport?: () => void,
 *   onPendingNavigate?: (continueNavigation: () => void) => void,
 * }} props
 */
export default function ConfigDetailPanel({
  catalogEntry,
  merchantId,
  queryState,
  onRefresh,
  onContactSupport,
}) {
  // Welcome card when no category is selected.
  if (!catalogEntry) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center">
        <h2 className="text-base font-semibold text-gray-900">
          Select a category to view its settings
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
          Pick a configuration category from the left to inspect every stored
          field for your account.
        </p>
      </div>
    );
  }

  return (
    <DetailPanelInner
      catalogEntry={catalogEntry}
      merchantId={merchantId}
      queryState={queryState}
      onRefresh={onRefresh}
      onContactSupport={onContactSupport}
    />
  );
}

/**
 * Inner panel - split out so we can early-return in the wrapper before
 * touching the EditState context (which requires a provider).
 */
function DetailPanelInner({
  catalogEntry,
  merchantId,
  queryState,
  onRefresh,
  onContactSupport,
}) {
  const { status, data, error, isFetching } = queryState;
  const { showToast } = useToast();
  const editState = useEditState();

  // Cancel-prompt visibility (only used while edit-mode + dirty when user
  // clicks Cancel - we ask before discarding).
  const [cancelPromptOpen, setCancelPromptOpen] = useState(false);

  // PATCH mutation
  const patchMutation = usePatchMerchantConfig({
    merchantId,
    configKey: catalogEntry.key,
    onSuccess: (envelope) => {
      editState.resetAfterSave(envelope);
      showToast("Configuration updated", "success", 2000);
    },
    onError: (err) => {
      editState.setServerError(err);
      editState.setSavingFlag(false);

      // INVALID_PARAMETER_VALUE → pin the error to the specific FieldRow so
      // the merchant sees a red border + inline message right next to the
      // offending input. The toolbar banner still shows the summary.
      if (
        err?.error_code === "INVALID_PARAMETER_VALUE" &&
        err?.error_context?.field_name
      ) {
        const path = parseFieldNameToPath(err.error_context.field_name);
        if (path) {
          const reason =
            err.error_context.reason ||
            err.error_message ||
            "Invalid value";
          editState.setFieldError(path, reason);
        }
      }

      showToast(
        humanizeErrorCode(err?.error_code) + " - see the panel for details",
        "error",
        2500
      );
    },
  });

  // Browser refresh guard while dirty.
  useBeforeUnloadGuard(editState.isDirty);

  // Sync original from the cache when a fresh GET arrives while in view mode.
  const valueField = catalogEntry.valueField;
  const liveValueBlock =
    data && valueField && data[valueField] ? data[valueField] : null;

  useEffect(() => {
    if (editState.mode === "view" && liveValueBlock) {
      editState.syncOriginal(liveValueBlock);
    }
    // Intentionally exclude editState from deps - context value identity
    // changes on every render, which would cause an infinite loop. The
    // callbacks inside are themselves stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveValueBlock, editState.mode]);

  // Loading-only state when no cached data is available yet.
  if (status === "loading" && !data) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title={catalogEntry.displayName}
          subtitle={catalogEntry.description}
        />
        <DetailPanelSkeleton />
      </div>
    );
  }

  // 404 / RECORD_NOT_FOUND → friendly not-provisioned state.
  if (status === "error" && error?.isNotFound) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title={catalogEntry.displayName}
          subtitle={catalogEntry.description}
        />
        <NotProvisionedEmptyState
          categoryDisplayName={catalogEntry.displayName}
          onContactSupport={onContactSupport}
        />
      </div>
    );
  }

  // Any other error → retryable inline error card.
  if (status === "error") {
    return (
      <div className="space-y-6">
        <DetailHeader
          title={catalogEntry.displayName}
          subtitle={catalogEntry.description}
        />
        <ErrorState error={error} onRetry={onRefresh} isRetrying={isFetching} />
      </div>
    );
  }

  // Success branch
  const isEmptyValueBlock =
    liveValueBlock &&
    typeof liveValueBlock === "object" &&
    !Array.isArray(liveValueBlock) &&
    Object.keys(liveValueBlock).length === 0;

  const renderValue =
    editState.mode === "edit" ? editState.draft : liveValueBlock;

  // Category-level allowlist gate - Edit button is hidden for read-only
  // categories regardless of payload state.
  const canEnterEdit =
    isCategoryEditable(catalogEntry.key) &&
    !isEmptyValueBlock &&
    !!liveValueBlock &&
    status === "success";

  const onEnterEdit = () => {
    editState.enterEditMode(liveValueBlock);
  };

  const onRequestCancel = () => {
    if (editState.isDirty) {
      setCancelPromptOpen(true);
      return;
    }
    editState.cancelEdit();
  };

  const onConfirmDiscard = () => {
    setCancelPromptOpen(false);
    editState.cancelEdit();
  };

  const onSave = async () => {
    if (editState.fieldErrors.size > 0) return;
    if (!editState.draft) return;
    const body = diffForPatch(editState.original, editState.draft);
    if (Object.keys(body).length === 0) {
      // Defensive - toolbar guards against this too.
      showToast("Nothing to save", "info", 1500);
      return;
    }
    editState.setServerError(null);
    editState.setSavingFlag(true);
    try {
      await patchMutation.mutateAsync(body);
    } catch {
      // onError above handles user-facing surfacing.
    }
  };

  return (
    <div className="space-y-5">
      <DetailHeader
        title={catalogEntry.displayName}
        subtitle={catalogEntry.description}
      />

      <AuditRibbon
        updatedDateTime={data?.updated_date_time}
        updatedBy={data?.updated_by}
        onRefresh={onRefresh}
        isRefetching={isFetching}
        refreshDisabled={editState.mode === "edit"}
        refreshDisabledReason={
          editState.mode === "edit"
            ? "Save or cancel your edits before refreshing"
            : undefined
        }
      />

      {/* Body: structured renderer */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
        {!liveValueBlock ? (
          <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-600">
            This configuration exists but no value block was returned. Contact
            support if this looks wrong.
          </div>
        ) : isEmptyValueBlock ? (
          <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-600">
            This configuration exists but has no values set yet. Contact
            support to configure it.
          </div>
        ) : (
          <ValueBlockRenderer
            value={renderValue}
            path={[]}
            depth={0}
            readOnly={editState.mode !== "edit"}
            topLevelLabel={humanizeLabel(catalogEntry.key)}
            configKey={catalogEntry.key}
          />
        )}
      </div>

      {/* Raw JSON escape hatch - hidden while editing to avoid confusion.  */}
      {editState.mode !== "edit" && <RawJsonToggle envelope={data} />}

      {/* Phase 2 footer toolbar (replaces the placeholder slot from Phase 1). */}
      <div data-phase2-footer-slot="merchant-config-detail">
        <EditToolbar
          canEnter={canEnterEdit}
          isFetching={isFetching}
          onEnter={onEnterEdit}
          onCancel={onRequestCancel}
          onSave={onSave}
          configKey={catalogEntry.key}
        />
      </div>

      <UnsavedChangesPrompt
        open={cancelPromptOpen}
        dirtyCount={editState.dirtyKeys.size}
        onCancel={() => setCancelPromptOpen(false)}
        onDiscard={onConfirmDiscard}
        onSave={() => {
          setCancelPromptOpen(false);
          onSave();
        }}
        canSave={editState.isDirty && editState.fieldErrors.size === 0}
      />
    </div>
  );
}

/* ---------- Sub-component: detail header band ---------- */

function DetailHeader({ title, subtitle }) {
  return (
    <header className="space-y-1">
      <h1 className="text-xl font-semibold text-gray-900 tracking-tight">
        {title}
      </h1>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </header>
  );
}
